import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';
import { Box, Stack, Typography, useMediaQuery, Button } from '@mui/material';
import { West as WestIcon } from '@mui/icons-material';

import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { SignatureCaptureDialog } from '@/components/shared/SignatureCaptureDialog';
import { IssuedItemsPane } from './IssuedItemsPane';
import { ReplacementPane } from './ReplacementPane';
import { ReturnCartPane } from './ReturnCartPane';
import { ExchangeCustomerSummaryModal } from './ExchangeCustomerSummaryModal';
import { useWorkflowSignatureCapture } from '../useWorkflowSignatureCapture';
import { useExchangeState } from './useExchangeState';
import type {
  ExchangeCartLine,
  ExchangeCartPair,
  ExchangeIssuedItem,
  ExchangeReplacementItem,
  ExchangeReturnDisposition,
} from './types';
import type { ExchangeReplacementOption } from '@/services/iem/types';
import {
  useExchangeReplacementOptions,
  usePHRItems,
  useTHRItems,
} from '@/hooks/queries/iem/useIEMQueries';
import { useNotifications } from '@/stores/useNotificationStore';
import { getErrorMessage } from '@/utils/httpClient';
import { getDefaultReplacementQty, getIssuedItemGroupKey } from '@/utils/iemIssuedItems';
import { EXCLUDE_PNP_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { getBaseNsn } from '@equip/utils';
import { usePersonLabels } from '@/hooks/usePersonLabels';

export interface ExchangeCompletionPayload {
  cartPairs: ExchangeCartPair[];
  cartLines: ExchangeCartLine[];
  capturedSignature?: string;
}

export interface IEMExchangeModalProps {
  open: boolean;
  onClose: () => void;
  unitName?: string;
  memberName?: string;
  customerId?: string;
  issuedItems?: ExchangeIssuedItem[];
  replacementItems?: ExchangeReplacementItem[];
  isCompleting?: boolean;
  onCompleteExchange?: (payload: ExchangeCompletionPayload) => void;
}

export const IEMExchangeModal = ({
  open,
  onClose,
  unitName,
  memberName,
  customerId,
  issuedItems,
  replacementItems,
  isCompleting = false,
  onCompleteExchange,
}: IEMExchangeModalProps) => {
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const iemLabels = iemConfig.features.front_window;
  const notifications = useNotifications();
  const person = usePersonLabels('front_window');
  const [customerSummaryOpen, setCustomerSummaryOpen] = useState(false);
  const {
    data: rawPhrData,
    isLoading: phrLoading,
    error: phrError,
  } = usePHRItems(customerId ?? '', EXCLUDE_PNP_TYPE_KEEP_NULL);
  const {
    data: rawThrData,
    isLoading: thrLoading,
    error: thrError,
  } = useTHRItems(customerId ?? '', EXCLUDE_PNP_TYPE_KEEP_NULL);
  const isIssuedLoading = phrLoading || thrLoading;

  // Stable empty arrays to prevent infinite loops
  const EMPTY_ISSUED_ITEMS: ExchangeIssuedItem[] = useMemo(() => [], []);
  const EMPTY_REPLACEMENT_ITEMS: ExchangeReplacementItem[] = useMemo(() => [], []);
  const EMPTY_SOURCE_ITEMS = useMemo(() => [], []);

  const phrData = rawPhrData ?? EMPTY_SOURCE_ITEMS;
  const thrData = rawThrData ?? EMPTY_SOURCE_ITEMS;

  const fetchedIssuedItems = useMemo<ExchangeIssuedItem[]>(() => {
    if (!customerId) {
      return EMPTY_ISSUED_ITEMS;
    }

    const toIssuedItem = (
      item: (typeof phrData)[number] | (typeof thrData)[number],
      receiptType: 'PHR' | 'THR',
    ): ExchangeIssuedItem => {
      const lin = item.lin?.trim() ?? '';
      const nsn = item.nsn?.trim() ?? '';
      const rawQty = 'qty' in item && typeof item.qty === 'number' ? item.qty : 1;
      const issuedQty = Math.max(0, rawQty);
      const baseNsn = getBaseNsn(nsn);
      return {
        id: item.id,
        lin: lin || '—',
        nsn: nsn || '—',
        baseNsn,
        catalogId: item.catalogId,
        parentCatalogId: item.parentCatalogId,
        name: item.item,
        issuedQty,
        issuedAt: item.issueDate,
        assetId: item.asset_id ?? undefined,
        receiptType,
        handReceiptId: item.id,
        unitPrice: item.actualPrice ?? item.unitPrice ?? null,
      };
    };

    const phrIssued = phrData
      .filter((item) => (item.status ?? '').toLowerCase() !== 'closed')
      .map((item) => toIssuedItem(item, 'PHR'));
    const thrIssued = thrData
      .filter((item) => (item.status ?? '').toLowerCase() !== 'closed')
      .map((item) => toIssuedItem(item, 'THR'));

    return [...phrIssued, ...thrIssued].sort((a, b) => a.name.localeCompare(b.name));
  }, [EMPTY_ISSUED_ITEMS, customerId, phrData, thrData]);

  const issuedItemsList = useMemo(
    () => (issuedItems?.length ? issuedItems : fetchedIssuedItems),
    [issuedItems, fetchedIssuedItems],
  );

  const issuedAssetIds = useMemo(
    () =>
      issuedItemsList
        .map((item) => item.assetId?.trim())
        .filter((assetId): assetId is string => Boolean(assetId)),
    [issuedItemsList],
  );

  // Collect unique catalog group IDs (parent catalog ID when present, otherwise
  // the item's own catalog ID). These identify the nomenclature family for each
  // issued item and are used for replacement lookup instead of LIN so that
  // items whose LIN changed since issue are still found (bug #29600).
  const issuedCatalogGroupIds = useMemo(() => {
    const unique = new Set<string>();
    issuedItemsList.forEach((item) => {
      const groupId = (
        item.parentCatalogId?.trim() ||
        item.catalogId?.trim() ||
        ''
      );
      if (groupId) {
        unique.add(groupId);
      }
    });
    return Array.from(unique).sort();
  }, [issuedItemsList]);

  const shouldFetchReplacements =
    open &&
    !replacementItems?.length &&
    issuedCatalogGroupIds.length > 0 &&
    Boolean(customerId) &&
    !isIssuedLoading;

  const {
    data: replacementOptions = [],
    isLoading: replacementsLoading,
    isFetching: replacementsFetching,
    error: replacementsError,
  } = useExchangeReplacementOptions({
    catalogGroupIds: issuedCatalogGroupIds,
    excludeAssetIds: issuedAssetIds,
    enabled: shouldFetchReplacements,
  }) as {
    data: ExchangeReplacementOption[];
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
  };

  const fetchedReplacementItems = useMemo<ExchangeReplacementItem[]>(() => {
    if (replacementOptions.length === 0) {
      return EMPTY_REPLACEMENT_ITEMS;
    }
    return replacementOptions.map((option) => {
      const trimmedNsn = option.nsn.trim();
      const normalizedNsn = trimmedNsn === '—' ? null : trimmedNsn;
      return {
        id: option.id,
        lin: option.lin,
        nsn: option.nsn,
        baseNsn: getBaseNsn(normalizedNsn),
        parentCatalogId: option.parentCatalogId,
        baseNomenclature: option.baseNomenclature,
        name: option.name,
        stock: option.stock,
        assetIds: option.assetIds,
      };
    });
  }, [replacementOptions, EMPTY_REPLACEMENT_ITEMS]);

  const replacementItemsList = useMemo(
    () => (replacementItems?.length ? replacementItems : fetchedReplacementItems),
    [replacementItems, fetchedReplacementItems],
  );

  const issuedError = phrError ?? thrError ?? null;

  useEffect(() => {
    if (!open || !customerId || !issuedError) {
      return;
    }
    notifications.error('Failed to load issued hand receipt items', getErrorMessage(issuedError));
  }, [open, customerId, issuedError, notifications]);

  useEffect(() => {
    if (!open || !customerId || !replacementsError) {
      return;
    }
    notifications.error('Failed to load replacement inventory', getErrorMessage(replacementsError));
  }, [open, customerId, replacementsError, notifications]);

  const isSideBySideLayout = useMediaQuery('(min-width:1300px)');

  const {
    focusedIssuedItemId,
    pairedReplacementByIssuedId,
    cartPairs,
    cartLines,
    replacementQtyByIssuedId,
    signatureAcknowledged,
    canComplete,
    focusIssuedItem,
    setReplacementQty,
    stageReplacement,
    removePair,
    setSignatureAcknowledged,
    reset,
  } = useExchangeState({ issuedItems: issuedItemsList, replacementItems: replacementItemsList });
  const {
    capturedSignature,
    signatureCaptureOpen,
    signaturePreviewSrc,
    openSignatureCapture,
    closeSignatureCapture,
    saveSignature,
    clearSignature,
    resetSignatureCapture,
  } = useWorkflowSignatureCapture({
    signatureAcknowledged,
    setSignatureAcknowledged,
    clearCapturedWhenUnacknowledged: true,
  });

  useEffect(() => {
    if (!open) {
      reset();
      resetSignatureCapture();
    }
  }, [open, reset, resetSignatureCapture]);

  const handleSignatureSaved = useCallback(
    (signatureBase64Png: string) => {
      saveSignature(signatureBase64Png);
      notifications.success('Signature captured', `${person.label} signature saved.`);
    },
    [notifications, saveSignature],
  );

  const focusedIssuedItem = useMemo(
    () => issuedItemsList.find((item) => item.id === focusedIssuedItemId) ?? null,
    [focusedIssuedItemId, issuedItemsList],
  );

  const pairedIssuedIds = useMemo(
    () => new Set(pairedReplacementByIssuedId.keys()),
    [pairedReplacementByIssuedId],
  );

  const replacementItemsForDisplay = useMemo(() => {
    if (replacementItemsList.length === 0) {
      return replacementItemsList;
    }

    if (cartPairs.length === 0) {
      return replacementItemsList;
    }

    const reservedCounts = new Map<string, number>();
    cartPairs.forEach((pair) => {
      reservedCounts.set(
        pair.replacementItemId,
        (reservedCounts.get(pair.replacementItemId) ?? 0) + 1,
      );
    });

    return replacementItemsList.map((item) => {
      const reserved = reservedCounts.get(item.id) ?? 0;
      const available = Math.max(item.assetIds.length - reserved, 0);
      if (available === item.stock) {
        return item;
      }
      return { ...item, stock: available };
    });
  }, [replacementItemsList, cartPairs]);

  const pairedReplacementIdForFocus = focusedIssuedItem
    ? (pairedReplacementByIssuedId.get(focusedIssuedItem.id) ?? null)
    : null;

  const subtitleParts = [unitName, memberName].filter(Boolean);
  const subtitle = subtitleParts.join(' • ');

  const getDesiredReplacementQty = useCallback(
    (item: ExchangeIssuedItem) => {
      const stored = replacementQtyByIssuedId[item.id];
      if (typeof stored === 'number') {
        return stored;
      }
      return getDefaultReplacementQty(item);
    },
    [replacementQtyByIssuedId],
  );

  const handleStageReplacement = useCallback(
    (replacementItemId: string, disposition: ExchangeReturnDisposition) => {
      if (!focusedIssuedItem) {
        return;
      }

      const groupKey = getIssuedItemGroupKey(focusedIssuedItem, { splitByReceiptType: true });
      const groupItems = issuedItemsList.filter(
        (item) => getIssuedItemGroupKey(item, { splitByReceiptType: true }) === groupKey,
      );

      if (groupItems.length === 0) {
        return;
      }

      const totalDesired = groupItems.reduce(
        (sum, item) => sum + getDesiredReplacementQty(item),
        0,
      );
      const alreadyPaired = groupItems.filter((item) =>
        pairedReplacementByIssuedId.has(item.id),
      ).length;

      let remainingToStage = Math.max(totalDesired - alreadyPaired, 0);
      if (remainingToStage <= 0) {
        return;
      }

      const pendingItems = groupItems.filter(
        (item) => getDesiredReplacementQty(item) > 0 && !pairedReplacementByIssuedId.has(item.id),
      );

      if (pendingItems.length === 0) {
        stageReplacement(focusedIssuedItem.id, replacementItemId, disposition);
        return;
      }

      for (const item of pendingItems) {
        if (remainingToStage <= 0) {
          break;
        }
        stageReplacement(item.id, replacementItemId, disposition);
        remainingToStage -= 1;
      }
    },
    [
      focusedIssuedItem,
      issuedItemsList,
      getDesiredReplacementQty,
      pairedReplacementByIssuedId,
      stageReplacement,
    ],
  );

  const handleCompleteExchange = () => {
    if (!canComplete) {
      return;
    }

    onCompleteExchange?.({
      cartPairs,
      cartLines,
      capturedSignature: capturedSignature ?? undefined,
    });
  };

  const isReplacementLoading =
    shouldFetchReplacements &&
    (replacementsLoading || (replacementsFetching && replacementOptions.length === 0));

  const plannerContent = (
    <Stack
      direction={isSideBySideLayout ? 'row' : 'column'}
      spacing={2}
      sx={{ minHeight: isSideBySideLayout ? 520 : 'auto' }}
      role="presentation"
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={1.5} sx={{ height: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Issued Items
          </Typography>
          <IssuedItemsPane
            items={issuedItemsList}
            focusedIssuedItemId={focusedIssuedItemId}
            onFocusItem={(issuedItemId) => focusIssuedItem(issuedItemId)}
            pairedIssuedIds={pairedIssuedIds}
            replacementQtyByIssuedId={replacementQtyByIssuedId}
            onUpdateReplacementQty={setReplacementQty}
          />
        </Stack>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={1.5} sx={{ height: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Replacement Options
          </Typography>
          <ReplacementPane
            linFilter={
              !focusedIssuedItem?.parentCatalogId &&
              !focusedIssuedItem?.catalogId &&
              focusedIssuedItem &&
              focusedIssuedItem.lin !== '—'
                ? focusedIssuedItem.lin
                : null
            }
            baseNsnFilter={focusedIssuedItem?.baseNsn ?? null}
            parentCatalogIdFilter={focusedIssuedItem?.parentCatalogId ?? null}
            catalogIdFilter={
              !focusedIssuedItem?.parentCatalogId && focusedIssuedItem?.catalogId
                ? focusedIssuedItem.catalogId
                : null
            }
            baseNomenclatureFilter={focusedIssuedItem?.name.replace(/\s*\([^)]+\)\s*$/, '') ?? null}
            replacementItems={replacementItemsForDisplay}
            pairedReplacementId={pairedReplacementIdForFocus ?? undefined}
            onStageReplacement={handleStageReplacement}
            isLoading={isReplacementLoading}
          />
        </Stack>
      </Box>
    </Stack>
  );

  const sidebarContent = (
    <ReturnCartPane
      cartLines={cartLines}
      signatureAcknowledged={signatureAcknowledged}
      signaturePreviewSrc={signaturePreviewSrc}
      onCaptureSignature={openSignatureCapture}
      onClearSignature={capturedSignature ? clearSignature : undefined}
      onShowCustomerSummary={() => setCustomerSummaryOpen(true)}
      onRemovePair={removePair}
      onCompleteExchange={handleCompleteExchange}
      canComplete={canComplete}
      isCompleting={isCompleting}
    />
  );

  const closeButton = (
    <Button
      variant="outlined"
      startIcon={<WestIcon />}
      onClick={onClose}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Return to Front-Window
    </Button>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={onClose}
        title={iemLabels.exchange_workflow_title}
        subTitle={subtitle}
        plannerTitle="Exchange Planner"
        plannerContent={plannerContent}
        sidebarTitle="Exchange Cart Summary"
        sidebarContent={sidebarContent}
        closeLabel="Close exchange workflow"
        closeButton={closeButton}
      />
      <SignatureCaptureDialog
        open={signatureCaptureOpen}
        title={`Capture ${person.label} Signature`}
        initialSignature={capturedSignature}
        onCancel={closeSignatureCapture}
        onSave={handleSignatureSaved}
      />
      <ExchangeCustomerSummaryModal
        open={customerSummaryOpen}
        onClose={() => setCustomerSummaryOpen(false)}
        unitName={unitName}
        memberName={memberName}
        cartLines={cartLines}
      />
    </>
  );
};
